/**
 * Hook para buscar endereço público a partir de um CEP brasileiro via
 * BrasilAPI (https://brasilapi.com.br/api/cep/v2/<cep>).
 *
 * **Por que BrasilAPI v2**: agrega múltiplas fontes (Correios, ViaCEP,
 * WideNet, Open CEP) com fallback automático — quando uma fonte está
 * fora, cai pra próxima. Isso importa pra nós: a Receita tem CEP
 * desatualizado em ~3-5% dos CNPJs BR, e o autofill via CNPJ por vezes
 * traz CEP que, ao ser consultado, retorna um logradouro diferente do
 * oficial. Ter CEP independente deixa a Karla corrigir o endereço em 2
 * cliques: apaga o CEP do autofill, digita o correto → rua/bairro/cidade/
 * UF vêm atualizados.
 *
 * **Reutilizado em 2 lugares** (espelhando `cnpj-lookup.ts`):
 *   - `LeadFormFields.tsx` — autofill em `NewLeadDialog` e
 *     `EditLeadDialog`.
 *   - `/onboarding/<token>` (PR-M1-01b) — cliente preenche CEP,
 *     recebe endereço pré-preenchido e só ajusta número/complemento.
 *
 * **Sem número e sem complemento**: esses campos nunca vêm do CEP
 * (são características do imóvel, não do logradouro). Ficam sempre
 * como input manual — nosso autofill NÃO toca neles.
 *
 * Implementação: React Query com cache por CEP, debounce natural via
 * `enabled` apenas quando CEP tem 8 dígitos. Cache por 7 dias — CEPs
 * mudam ainda menos que CNPJs.
 */
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { normalizeCep } from './formatters.js';

/** Subset do retorno BrasilAPI v2 que usamos. Documentação oficial:
 * https://brasilapi.com.br/docs#tag/CEP-V2. Campos `location` (lat/lng)
 * e `service` (qual fonte respondeu) são ignorados via passthrough. */
const brasilApiCepSchema = z
  .object({
    cep: z.string(),
    state: z.string().nullable().optional(),
    city: z.string().nullable().optional(),
    neighborhood: z.string().nullable().optional(),
    street: z.string().nullable().optional(),
    service: z.string().nullable().optional(),
  })
  .passthrough();

export type CepLookupResult = z.infer<typeof brasilApiCepSchema>;

/**
 * Hook para o autofill por CEP. Retorna os dados do endereço quando o
 * input tem 8 dígitos; caso contrário `isIdle` = true e não faz fetch.
 *
 * Estado exposto segue o padrão React Query: `isLoading`, `isError`,
 * `error`, `data`. O consumidor decide onde mostrar feedback.
 */
export function useCepLookup(cepInput: string | null | undefined) {
  const normalized = normalizeCep(cepInput);
  const enabled = normalized.length === 8;

  return useQuery({
    queryKey: ['cep-lookup', normalized],
    enabled,
    // CEPs são bem estáveis — bairro/rua raramente mudam. 7 dias de
    // cache reduz chamadas à API em fluxos de retomada de cadastro.
    staleTime: 7 * 24 * 60 * 60 * 1_000,
    retry: 1,
    queryFn: async (): Promise<CepLookupResult> => {
      const response = await fetch(`https://brasilapi.com.br/api/cep/v2/${normalized}`, {
        headers: { accept: 'application/json' },
      });
      if (response.status === 404) {
        throw new Error('CEP não encontrado. Confira os dígitos.');
      }
      if (response.status === 429) {
        throw new Error('Muitas consultas de CEP em seguida. Aguarde alguns segundos.');
      }
      if (!response.ok) {
        throw new Error(`Falha ao consultar CEP (HTTP ${response.status}).`);
      }
      const raw = await response.json();
      return brasilApiCepSchema.parse(raw);
    },
  });
}
